import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/lib/utils";

const variantStyles = {
  primary:
    "bg-primary text-paper hover:bg-primary-dark active:bg-primary-dark",
  secondary:
    "bg-secondary text-white hover:bg-secondary/90 active:bg-secondary/80",
  outline:
    "border border-primary text-primary bg-transparent hover:bg-primary/[0.06] active:bg-primary/10",
  ghost:
    "text-primary bg-transparent underline-offset-4 hover:bg-primary/[0.06] hover:underline active:bg-primary/10",
} as const;

const sizeStyles = {
  sm: "min-h-[44px] px-4 py-2 text-sm gap-1.5",
  md: "min-h-[44px] px-6 py-2.5 text-base gap-2",
  lg: "min-h-[52px] px-8 py-3 text-lg gap-2.5",
} as const;

type Variant = keyof typeof variantStyles;
type Size = keyof typeof sizeStyles;

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant */
  variant?: Variant;
  /** Size preset */
  size?: Size;
  /**
   * When true, the component renders its child directly instead of a
   * `<button>`, merging props downward. Useful for wrapping Next.js `<Link>`.
   */
  asChild?: boolean;
}

/**
 * Primary interactive element. Supports `asChild` so it can wrap `<Link>`
 * without producing an invalid `<button><a>` nesting.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      asChild = false,
      className,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Component = asChild ? Slot : "button";

    return (
      <Component
        ref={ref}
        disabled={disabled}
        className={cn(
          // Base
          "inline-flex items-center justify-center rounded-md font-semibold",
          "whitespace-nowrap transition-colors duration-150",
          // Focus
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary",
          // Disabled
          "disabled:pointer-events-none disabled:opacity-50",
          // Variant + size
          variantStyles[variant],
          sizeStyles[size],
          className,
        )}
        {...props}
      >
        {children}
      </Component>
    );
  },
);

Button.displayName = "Button";

export { Button };
export type { ButtonProps, Variant as ButtonVariant, Size as ButtonSize };
